// utils/changelog.ts
/**
 * Reads CHANGELOG.md (Keep a Changelog) into releases and their groups, for the build badge and
 * the /changelog page. Only the subset the file uses: `##` releases, `###` groups (Added,
 * Changed…), `- ` items with indented continuation lines, paragraphs, and `<!-- -->` comments
 * (dropped). Inline marks are left
 * in the text for `ChangelogText` to draw.
 */

export interface ChangelogGroup {
    heading: string;
    items: string[];
}

export interface ChangelogRelease {
    /** The heading without its brackets: "Unreleased" or a release date */
    title: string;
    notes: string[];
    groups: ChangelogGroup[];
}

export interface Changelog {
    intro: string[];
    releases: ChangelogRelease[];
}

export const parseChangelog = (text: string): Changelog => {
    const log: Changelog = { intro: [], releases: [] };
    let release: ChangelogRelease | null = null;
    let group: ChangelogGroup | null = null;
    // Where the next plain line goes: a paragraph being built, or the last item continuing
    let paragraph: string[] | null = null;
    let itemOpen = false;

    const flush = () => {
        // A paragraph under a group heading still belongs to its release
        if (paragraph?.length) (release?.notes ?? log.intro).push(paragraph.join(' '));
        paragraph = null;
    };

    // Notes for whoever edits the file sit in <!-- --> comments, which no reader should see
    const visible = text.replace(/<!--[\s\S]*?-->/g, '');

    for (const raw of visible.split(/\r?\n/)) {
        const line = raw.trimEnd();
        if (line.startsWith('# ')) continue;
        if (line.startsWith('## ')) {
            flush();
            release = {
                title: line
                    .slice(3)
                    .trim()
                    .replace(/^\[(.*)\]$/, '$1'),
                notes: [],
                groups: [],
            };
            log.releases.push(release);
            group = null;
            itemOpen = false;
        } else if (line.startsWith('### ')) {
            flush();
            group = { heading: line.slice(4).trim(), items: [] };
            release?.groups.push(group);
            itemOpen = false;
        } else if (/^[-*] /.test(line)) {
            flush();
            if (group) {
                group.items.push(line.slice(2).trim());
                itemOpen = true;
            }
        } else if (!line.trim()) {
            flush();
            itemOpen = false;
        } else if (itemOpen && group && /^\s/.test(raw)) {
            group.items[group.items.length - 1] += ` ${line.trim()}`;
        } else {
            itemOpen = false;
            (paragraph ??= []).push(line.trim());
        }
    }
    flush();
    return log;
};

/** The release still to come, if the file has one */
export const unreleasedOf = (log: Changelog): ChangelogRelease | undefined =>
    log.releases.find((r) => r.title.toLowerCase() === 'unreleased');

export type InlinePart =
    | { kind: 'text'; text: string }
    | { kind: 'strong'; text: string }
    | { kind: 'code'; text: string }
    | { kind: 'link'; text: string; href: string };

/** Splits a line into plain text, **bold**, `code` and [links](url); nothing else is markup */
export const parseInline = (text: string): InlinePart[] => {
    const parts: InlinePart[] = [];
    const pattern = /\*\*(.+?)\*\*|`([^`]+)`|\[([^\]]+)\]\(([^)\s]+)\)/g;
    let last = 0;
    for (const m of text.matchAll(pattern)) {
        const at = m.index ?? 0;
        if (at > last) parts.push({ kind: 'text', text: text.slice(last, at) });
        if (m[1] !== undefined) parts.push({ kind: 'strong', text: m[1] });
        else if (m[2] !== undefined) parts.push({ kind: 'code', text: m[2] });
        else parts.push({ kind: 'link', text: m[3], href: m[4] });
        last = at + m[0].length;
    }
    if (last < text.length) parts.push({ kind: 'text', text: text.slice(last) });
    return parts;
};
